import React from 'react';
import {useAuthStateManager} from '../../hooks/useAuthStateManager.tsx';
import {Page} from '../../pages.ts';
import PageLayout from './PageLayout.ts';
import AppHeader from '../ds/AppHeader.tsx';
import DraggableTranslationPanel from '../translation/DraggableTranslationPanel.tsx';
import {useTranslation} from '../translation/TranslationContext.tsx';
import FloatingTranslationButton from '../translation/FloatingTranslationButton.tsx';

interface MainLayoutProps {
    children: React.ReactNode;
    page: Page;
}

const MainLayout: React.FC<MainLayoutProps> = ({children, page}) => {
    const {isAuthenticated} = useAuthStateManager();

    const {showTranslationPanel, setShowTranslationPanel, projectInfo} =
        useTranslation();

    const handleTranslationToggle = () => {
        setShowTranslationPanel(!showTranslationPanel);
    };

    const getTranslation = () => {
        if (!page.translation) {
            return null;
        }

        if (!isAuthenticated) {
            return null;
        }

        return (
            <>
                <FloatingTranslationButton onClick={handleTranslationToggle} />
                {showTranslationPanel && (
                    <DraggableTranslationPanel
                        onClose={() => setShowTranslationPanel(false)}
                        projectInfo={projectInfo}
                    />
                )}
            </>
        );
    };

    if (page.pageLayout === PageLayout.None) {
        return <>{children}</>;
    }

    if (page.pageLayout === PageLayout.FullScreen) {
        return (
            <div id="fullscreen-content">
                {getTranslation()}
                {children}
            </div>
        );
    }

    // Projects and groups draw their own top bar in place of the app header.
    if (
        page.pageLayout === PageLayout.Project ||
        page.pageLayout === PageLayout.Group
    ) {
        return (
            <div className="layout-holder">
                <main>
                    {getTranslation()}
                    {children}
                </main>
            </div>
        );
    }

    return (
        <div>
            {getTranslation()}
            <AppHeader />
            <main className="mx-main">{children}</main>
        </div>
    );
};

export default MainLayout;
